import { useMediaQuery } from "@mui/material";
import React from "react";
import { theme } from "@/theme";
import { Carousel } from "@/components/carousel";
import { ProductCardProduct, ProductCard } from "./ProductCard";
import {
  ShoppableProductCard,
  ShoppableProductCardProduct,
} from "./ShoppableProductCard";
import { SkeletonData, SkeletonProductCard } from "./SkeletonProductCard";
import { cn } from "@/helpers/utils";

type ProductCardListBaseProps = {
  layout?: "grid" | "carousel" | "flex";
};
export type ProductCardListProps =
  | ({
      products: (ShoppableProductCardProduct | SkeletonData)[];
      shoppable: true | undefined;
    } & ProductCardListBaseProps)
  | ({
      products: (ProductCardProduct | SkeletonData)[];
      shoppable: false;
    } & ProductCardListBaseProps);

export const ProductCardList = (props: ProductCardListProps) => {
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );
  const isCarousel = props?.layout !== "grid" && props?.layout !== "flex";
  const isFlexLayout = props?.layout === "flex";
  const productCardProps = {
    className: cn(
      "h-full hover:shadow-lg",
      isSmallScreen &&
        isCarousel &&
        "shadow-lg border border-solid border-transparent hover:border-primary-200"
    ),
    truncateText: isCarousel,
  };

  const productCards = props.shoppable
    ? props.products.map((product) => {
        const isSkeleton = "imageHeight" in product;
        return isSkeleton ? (
          <SkeletonProductCard
            className={productCardProps.className}
            productCardValue={product}
          />
        ) : (
          <ShoppableProductCard
            {...productCardProps}
            product={product}
            showRatings={!product?.product?.tags?.includes("hide-reviews")}
          />
        );
      })
    : props.products.map((product) => {
        const isSkeleton = "imageHeight" in product;
        return isSkeleton ? (
          <SkeletonProductCard
            className={productCardProps.className}
            productCardValue={product}
          />
        ) : (
          <ProductCard {...productCardProps} productCardValue={product} />
        );
      });

  if (isCarousel && isSmallScreen) {
    const slides = productCards.map((elem, i) => (
      <div key={i} className="mb-6 mt-1 w-full max-w-full px-12">
        {elem}
      </div>
    ));

    return (
      <Carousel
        items={slides}
        dots="none"
        slidePosition="stretch"
        slideGap={false}
        arrows={{ gutter: "none", variant: "blue-icon" }}
      />
    );
  }

  const items = productCards.map((elem, i) => (
    <li key={i} className="max-w-full">
      {elem}
    </li>
  ));

  if (isCarousel) {
    // Carousel layout
    return (
      <ul className="-m-4 grid grid-cols-1 gap-4 gap-y-8 md:grid-cols-2 lg:grid-cols-4">
        {items}
      </ul>
    );
  } else if (isFlexLayout) {
    // Flex layout
    return (
      <div className="flex flex-wrap justify-center">
        {items.map((elem, i) => (
          <div key={i} className="flex-1">
            {elem}
          </div>
        ))}
      </div>
    );
  } else {
    // Default to grid layout if not carousel or flex
    return (
      <ul className="-m-4 grid grid-cols-2 gap-y-6 sm:gap-2 md:gap-4 md:gap-y-8 lg:grid-cols-4">
        {items}
      </ul>
    );
  }
};
